import React from 'react';
import { TableCell, TableHead, TableRow, Box } from '@mui/material';
import ResizeHandle from './ResizeHandle';

export interface ColumnConfig {
  key: string;
  label: string;
  sortable?: boolean;
  resizable?: boolean;
  minWidth?: number;
}

interface DataTableHeaderProps {
  columns: ColumnConfig[];
  visibleColumns: string[];
  getColumnWidth: (key: string) => number;
  onColumnResize: (key: string, newWidth: number) => void;
  onSort?: (columnKey: string) => void;
  getSortIndicator?: (columnKey: string) => string | null;
}

/**
 * Reusable table header component with sort and resize functionality.
 * Handles column rendering with proper widths and interactive features.
 *
 * @example
 * <DataTableHeader
 *   columns={columns}
 *   visibleColumns={visibleColumns}
 *   getColumnWidth={tablePrefs.getColumnWidth}
 *   onColumnResize={handleResize}
 *   onSort={handleSort}
 *   getSortIndicator={getSortIndicator}
 * />
 */
const DataTableHeader: React.FC<DataTableHeaderProps> = ({
  columns,
  visibleColumns,
  getColumnWidth,
  onColumnResize,
  onSort,
  getSortIndicator,
}) => {
  const getVisibleColumnsInOrder = () => {
    return visibleColumns
      .map((key) => columns.find((c) => c.key === key))
      .filter((col) => col !== undefined) as ColumnConfig[];
  };

  const handleHeaderClick = (column: ColumnConfig) => {
    if (column.sortable && onSort) {
      onSort(column.key);
    }
  };

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: 'grey.50' }}>
        {getVisibleColumnsInOrder().map((column) => {
          const width = getColumnWidth(column.key);
          const isSortable = column.sortable ?? false;
          const sortIndicator = getSortIndicator ? getSortIndicator(column.key) : null;

          return (
            <TableCell
              key={column.key}
              sx={{
                width: `${width}px`,
                minWidth: `${width}px`,
                maxWidth: `${width}px`,
                cursor: isSortable ? 'pointer' : 'default',
                '&:hover': isSortable ? { backgroundColor: '#eeeeee' } : {},
                padding: '12px 8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'grey.50',
                userSelect: 'none',
              }}
              onClick={() => handleHeaderClick(column)}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <span title={column.label}>{column.label}</span>
                {sortIndicator && (
                  <span style={{ color: '#2196F3', fontSize: '0.875rem', flexShrink: 0 }}>
                    {sortIndicator}
                  </span>
                )}
              </Box>

              {/* Resize handle for resizable columns */}
              {column.resizable && (
                <ResizeHandle
                  columnKey={column.key}
                  width={width}
                  minWidth={column.minWidth || 80}
                  onResize={onColumnResize}
                />
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default DataTableHeader;
